import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared visual language for the app's "control deck" toolbars — the elevated bar
 * of compact chip controls first introduced for the recall map/list filter bar
 * (RecallFilterBar) and reused by the routing toolbars so every page reads as the
 * same product surface. This module owns ONLY presentation (class strings + tiny
 * presentational components); it has no feature logic and no state.
 */

/** Trigger sizing for a compact chip control. */
export const TRIGGER = 'h-8 text-xs w-auto min-w-[7rem] gap-1';

/** Leading search icon inside a chip-sized text input. */
export const SEARCH_ICON = 'absolute left-2 top-2.5 h-4 w-4 text-muted-foreground';

// Controls sit as lighter "chips" inside the bar: white in light mode, a step
// LIGHTER than the dark bar in dark mode (the bar is brand-neutral-800). In dark
// mode we rely on that lighter card + the bar's shadow for separation rather than
// a hard outline (border goes transparent). Hover gently lightens the field
// (orange is reserved for active/focus); a quick transition keeps it premium.
export const CONTROL = cn(
  'bg-white dark:bg-brand-neutral-700',
  'border-brand-neutral-200 dark:border-transparent',
  'transition-colors duration-150',
  'hover:bg-brand-primary-50 dark:hover:bg-brand-neutral-600'
);

// Option rows in dropdowns use the same soft warm hover, not the blue accent.
export const OPTION_ROW = cn(
  'flex items-center space-x-2 py-1.5 px-2 rounded-sm cursor-pointer',
  'hover:bg-brand-primary-50 dark:hover:bg-brand-primary-500/10'
);

// Active controls get a warm-orange border; the value pill carries the emphasis.
export const activeCls = (active: boolean) =>
  active ? 'border-brand-primary-400 dark:border-brand-primary-500' : '';

// The elevated card SURFACE only — soft warm sand in light mode, warm charcoal a
// step lighter than the near-black shell at 95% with a touch of blur in dark mode,
// so the bar reads as a layered "control deck", not a flat slab. Layout (flex/gap)
// and spacing (m-2 / px / py) are intentionally left to each caller: the recall bar
// floats with `m-2`, while a contained sub-bar inside a page header does not.
export const CONTROL_DECK = cn(
  'rounded-xl shadow-lg',
  'border border-brand-neutral-200 bg-brand-neutral-100',
  'dark:border-brand-neutral-700 dark:bg-brand-neutral-800/95 dark:backdrop-blur-sm'
);

/** Subtle entrance: ease up + fade in on first load. Use for floating decks. */
export const CONTROL_DECK_ENTER = 'animate-in fade-in slide-in-from-top-3 duration-300';

/** Muted "section" label inside a deck (e.g. the filter/icon + field name). */
export const SECTION_LABEL =
  'flex items-center gap-1.5 pr-1 text-xs font-semibold text-muted-foreground';

/** A bronze emphasis pill (e.g. "Available", "3 selected", "5 assigned") that
 *  makes an active value read at a glance. Re-keyed on `value` so it fades/slides
 *  the new selection in. */
export const ValuePill: React.FC<{ value: string }> = ({ value }) => (
  <span
    key={value}
    className={cn(
      'inline-flex h-4 max-w-[8rem] items-center truncate rounded-full px-1.5',
      'text-[10px] font-semibold',
      'bg-brand-primary-100 text-brand-primary-900',
      'dark:bg-brand-primary-600 dark:text-brand-primary-50',
      // Fade/slide the new value in when the selection changes.
      'animate-in fade-in-0 slide-in-from-left-1 duration-150'
    )}
  >
    {value}
  </span>
);

/** A control trigger's inner content: muted field label + an optional bronze
 *  value pill (e.g. "Status: Available") so active filters read at a glance. */
export const ControlLabel: React.FC<{ label: string; value?: string | undefined }> = ({
  label,
  value
}) => (
  // `!flex` overrides shadcn SelectTrigger's `[&>span]:line-clamp-1`, which forces
  // display:-webkit-box on the trigger's child span. In Safari that vertical box
  // clips everything after the label, hiding the value pill (Chrome is lenient).
  <span className='!flex items-center gap-1.5 truncate'>
    <span className='text-muted-foreground'>{label}</span>
    {value && <ValuePill value={value} />}
  </span>
);
